import { useState } from 'react';
// material
import {
  Box,
  Card,
  Table,
  Stack,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Chip,
  Button,
  Tooltip,
  TextField,
  InputAdornment,
  Alert,
} from '@mui/material';
// components
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../components/SearchNotFound';
import { UserListHead } from '../sections/@dashboard/user';
import useCommands from '../hooks/useCommands';
import { useLanguage } from '../context/LanguageContext';
import useTextReplacement from '../hooks/useTextReplacement';
import Iconify from '../components/Iconify';
import AddCommandPopup from '../ui-component/AddCommandPopup';
import TextCommandLibraryDialog from '../ui-component/TextCommandLibraryDialog';
import { useAuth } from '../context/AuthContext';
import AlertForPremium from '../ui-component/AlertForPremium';
import { updateUser } from '../services/firebaseService';
import constants from '../constants';
import { getUsageAccessInfo } from '../services/helper';

// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return array.filter((command) => {
      const commandName = Array.isArray(command.match)
        ? command.match.join(', ').toLowerCase()
        : command.match.toLowerCase();
      return commandName.indexOf(query.toLowerCase()) !== -1;
    });
  }
  return stabilizedThis.map((el) => el[0]);
}

function getCommandName(command) {
  return Array.isArray(command.match) ? command.match.join(', ') : command.match;
}

function getCommandMatches(command) {
  return Array.isArray(command.match) ? command.match : [command.match];
}

export default function Commands() {
  const { t } = useLanguage();
  const { currentUser } = useAuth();
  const defaultCommands = useCommands();
  const defaultTextReplacements = useTextReplacement();
  const [customCommands, setCustomCommands] = useState(
    currentUser.commands ? currentUser.commands.map((cmd) => ({ ...cmd, isCustom: true })) : []
  );
  const [commands, setCommands] = useState([...customCommands, ...defaultCommands, ...defaultTextReplacements]);
  const [commandPopUpOpened, setCommandPopUpOpened] = useState(false);
  const [libraryPopUpOpened, setLibraryPopUpOpened] = useState(false);
  const [libraryCommandQueue, setLibraryCommandQueue] = useState([]);
  const [activeLibraryCommand, setActiveLibraryCommand] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [deleting, setDeleting] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState(
    applySortFilter(commands, getComparator(order, orderBy), filterName)
  );
  const accessInfo = getUsageAccessInfo(currentUser);
  const commandsTitle = t('commands.title'); // Commands
  const name = t('name'); // Name
  const desc = t('desc'); // Description
  const type = t('type'); // Type
  const lCustom = t('lCustom'); // Custom
  const lDefault = t('lDefault'); // Default

  const addCommandTip = t('buyPremiumToAddCc'); // Buy premium to add custom Commands
  const customCommandsCount = Array.isArray(currentUser.commands) ? currentUser.commands.length : 0;
  const freeSlots = Math.max(constants.freeCustomCommands - customCommandsCount, 0);
  const existingMatches = commands.flatMap(getCommandMatches).filter(Boolean);
  const TABLE_HEAD = [
    { id: 'name', label: name, alignRight: false },
    { id: 'description', label: desc, alignRight: false },
    { id: 'type', label: type, alignRight: true },
  ];

  const addNewCommand = () => {
    if (!accessInfo.paid && customCommandsCount >= constants.freeCustomCommands) {
      setShowPay(true);
      return;
    }
    setLibraryCommandQueue([]);
    setActiveLibraryCommand(null);
    setCommandPopUpOpened(true);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    const nextOrder = isAsc ? 'desc' : 'asc';
    setOrder(nextOrder);
    setOrderBy(property);
    setFilteredUsers(applySortFilter(commands, getComparator(nextOrder, property), filterName));
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = filteredUsers
        .filter((cmd) => cmd.isCustom)
        .map((cmd) => getCommandName(cmd));
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    const nextFilterName = event.target.value;
    setFilterName(nextFilterName);
    setPage(0);
    setFilteredUsers(applySortFilter(commands, getComparator(order, orderBy), nextFilterName));
  };

  const deleteSelectedCommands = () => {
    setDeleting(true);
    const allCustomCommands = currentUser.commands ? currentUser.commands : [];
    const remainingCustomCommands = allCustomCommands
      .filter((cmd) => !selected.includes(getCommandName(cmd)))
      .map((cmd) => ({ ...cmd, isCustom: true }));
    updateUser(currentUser.email, { commands: remainingCustomCommands }).then(() => {
      currentUser.commands = remainingCustomCommands;
      const allCommands = [
        ...remainingCustomCommands.map((cmd) => ({ ...cmd, isCustom: true })),
        ...defaultCommands,
        ...defaultTextReplacements,
      ];
      setCommands(allCommands);
      setCustomCommands(remainingCustomCommands.map((cmd) => ({ ...cmd, isCustom: true })));
      setFilteredUsers(applySortFilter(allCommands, getComparator(order, orderBy), filterName));
      setDeleting(false);
      setSelected([]);
    });
  };

  const saveCommand = async (command) => {
    const remainingLibraryQueue = activeLibraryCommand ? libraryCommandQueue.slice(1) : [];
    const keepEditingLibrary = remainingLibraryQueue.length > 0;
    const allCommandsCustomCommands = currentUser.commands ? [...currentUser.commands] : [];
    allCommandsCustomCommands.unshift(command);
    currentUser.commands = allCommandsCustomCommands;
    const allCommands = [
      ...allCommandsCustomCommands.map((cmd) => ({ ...cmd, isCustom: true })),
      ...defaultCommands,
      ...defaultTextReplacements,
    ];
    setCommands(allCommands);
    setFilteredUsers(applySortFilter(allCommands, getComparator(order, orderBy), filterName));
    await updateUser(currentUser.email, { commands: allCommandsCustomCommands });
    setCustomCommands(allCommandsCustomCommands);
    if (keepEditingLibrary) {
      setLibraryCommandQueue(remainingLibraryQueue);
      setActiveLibraryCommand(remainingLibraryQueue[0]);
      return { keepOpen: true };
    }
    setLibraryCommandQueue([]);
    setActiveLibraryCommand(null);
    setCommandPopUpOpened(false);
    return { keepOpen: false };
  };

  const openLibraryCommandsForEditing = (libraryCommands) => {
    const selectedLibraryCommands = libraryCommands.map((cmd) => ({
      match: cmd.match.trim().toLowerCase(),
      description: cmd.description,
      useComputer: false,
    }));
    if (!accessInfo.paid && selectedLibraryCommands.length > freeSlots) {
      setShowPay(true);
      return;
    }
    setLibraryPopUpOpened(false);
    setLibraryCommandQueue(selectedLibraryCommands);
    setActiveLibraryCommand(selectedLibraryCommands[0]);
    setCommandPopUpOpened(true);
  };

  const isUserNotFound = filteredUsers.length === 0;

  return (
    <>
      <AlertForPremium
        open={showPay}
        onClose={() => setShowPay(false)}
        error={{ message: 'Please upgrade to create unlimited Custom Commands!' }}
      />
      <AddCommandPopup
        commands={commands}
        onSave={saveCommand}
        initialCommand={activeLibraryCommand}
        helperText={
          activeLibraryCommand
            ? `Review this library shortcut before saving. ${libraryCommandQueue.length} selected template${libraryCommandQueue.length === 1 ? '' : 's'} remaining.`
            : ''
        }
        open={commandPopUpOpened}
        onClose={() => {
          setLibraryCommandQueue([]);
          setActiveLibraryCommand(null);
          setCommandPopUpOpened(false);
        }}
      />
      <TextCommandLibraryDialog
        open={libraryPopUpOpened}
        onClose={() => setLibraryPopUpOpened(false)}
        onEditSelected={openLibraryCommandsForEditing}
        existingMatches={existingMatches}
        freeSlots={freeSlots}
        isPaid={accessInfo.paid}
      />
      <Page title={commandsTitle}>
        <Container maxWidth="lg">
          <Stack spacing={3}>
            <Card
              sx={{
                p: { xs: 3, md: 4 },
                color: 'common.white',
                overflow: 'hidden',
                position: 'relative',
                background: 'linear-gradient(135deg, #171717 0%, #23221F 58%, #0F3D28 100%)',
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={3}>
                <Box sx={{ maxWidth: 660 }}>
                  <Chip label="Voice command library" color="secondary" sx={{ mb: 2, fontWeight: 800 }} />
                  <Typography variant="h3" sx={{ color: 'common.white' }}>
                    Make repeated typing disappear.
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.72)', mt: 1.5, lineHeight: 1.8 }}>
                    Create short voice cues for snippets, formatting, replacements, and AI-assisted writing.
                  </Typography>
                </Box>
                <Tooltip title={!accessInfo.paid ? addCommandTip : ''}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
                  >
                    <Button
                      size="large"
                      variant="outlined"
                      startIcon={<Iconify icon="solar:library-bold-duotone" />}
                      onClick={() => setLibraryPopUpOpened(true)}
                      sx={{
                        height: 50,
                        minWidth: { xs: '100%', sm: 170 },
                        px: 2.5,
                        color: 'common.white',
                        borderColor: 'rgba(255,255,255,0.32)',
                        whiteSpace: 'nowrap',
                        '& .MuiButton-startIcon': { mr: 1 },
                        '&:hover': { borderColor: 'common.white', bgcolor: 'rgba(255,255,255,0.08)' },
                      }}
                    >
                      Import library
                    </Button>
                    <Button
                      size="large"
                      variant="contained"
                      startIcon={<Iconify icon="solar:magic-stick-3-bold-duotone" />}
                      onClick={addNewCommand}
                      sx={{
                        height: 50,
                        minWidth: { xs: '100%', sm: 170 },
                        px: 2.5,
                        bgcolor: 'common.white',
                        color: 'primary.main',
                        whiteSpace: 'nowrap',
                        '& .MuiButton-startIcon': { mr: 1 },
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                      }}
                    >
                      Add command
                    </Button>
                  </Stack>
                </Tooltip>
              </Stack>
            </Card>

            {!accessInfo.paid && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Free accounts can create {constants.freeCustomCommands} custom commands. You have {freeSlots} left.
              </Alert>
            )}

            <Card sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ md: 'center' }}
                spacing={2}
                sx={{ p: 2.5 }}
              >
                <TextField
                  fullWidth
                  value={filterName}
                  onChange={handleFilterByName}
                  placeholder="Search voice commands"
                  sx={{ maxWidth: 420 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-fill" width={20} height={20} />
                      </InputAdornment>
                    ),
                  }}
                />
                {selected.length > 0 && (
                  <Button
                    color="error"
                    variant="outlined"
                    disabled={deleting}
                    onClick={deleteSelectedCommands}
                    startIcon={<Iconify icon="eva:trash-2-outline" />}
                  >
                    Delete {selected.length}
                  </Button>
                )}
              </Stack>
            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table sx={{ '& th': { bgcolor: 'background.default', color: 'text.secondary', fontWeight: 800 }, '& td': { py: 2 } }}>
                  <UserListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={filteredUsers.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                    customCommandsLen={customCommands.length}
                  />
                  <TableBody>
                    {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                      const { id, description, isNew, isCustom } = row;
                      const commandName = getCommandName(row);
                      const isItemSelected = selected.indexOf(commandName) !== -1;

                      return (
                        <TableRow
                          hover
                          key={id}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                          sx={{
                            '&:last-of-type td': { borderBottom: 0 },
                            '&:hover': { bgcolor: 'background.default' },
                          }}
                        >
                          <TableCell padding="checkbox">
                            {isCustom && (
                              <Checkbox checked={isItemSelected} onChange={(event) => handleClick(event, commandName)} />
                            )}
                          </TableCell>
                          <TableCell component="th" scope="row" padding="none">
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Box
                                sx={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: 1.5,
                                  bgcolor: isCustom ? 'secondary.lighter' : 'background.neutral',
                                  color: isCustom ? 'secondary.dark' : 'text.secondary',
                                  display: 'grid',
                                  placeItems: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                <Iconify icon={isCustom ? 'solar:magic-stick-3-bold-duotone' : 'solar:command-bold-duotone'} width={18} height={18} />
                              </Box>
                              <Typography variant="subtitle2">
                                {commandName}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="left">
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {description} {isNew && <Chip label="New" size="small" variant="outlined" color="info" />}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {isCustom ? <Chip variant="outlined" color="secondary" label={lCustom} /> : <Chip variant="outlined" label={lDefault} />}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>

                  {isUserNotFound && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                          <SearchNotFound searchQuery={filterName} />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Card>
          </Stack>
        </Container>
      </Page>
    </>
  );
}
